import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useUser, useAuth } from '@clerk/clerk-react';
import { Building2, Check, AlertCircle } from 'lucide-react';
import AdminAnalytics from './AdminAnalytics';
import { BarChart3 } from 'lucide-react';

const AdminDashboard = () => {
  const { user } = useUser();
  const [activeTab, setActiveTab] = useState('facilities');
  const { getToken } = useAuth();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    facility_id: '',
    name: '',
    city: '',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    booking_url: '',
    address: '',
    latitude: '',
    longitude: '',
    supported_types: [],
    features: [],
    rating: 4.0
  });

  const [message, setMessage] = useState({ type: '', text: '' });
  const [loading, setLoading] = useState(false);

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
  const colors = {
    primary: '#00BCD4',
    success: '#10B981',
    error: '#EF4444'
  };

  // Check if user is admin
  useEffect(() => {
    if (user && user.publicMetadata?.role !== 'admin') {
      alert('Access denied - Admin only');
      navigate('/');
    }
  }, [user, navigate]);

  // Google Places Autocomplete
  const initAutocomplete = () => {
    const input = document.getElementById('address-input');
    if (!input || !window.google) return;

    const autocomplete = new window.google.maps.places.Autocomplete(input, {
      componentRestrictions: { country: 'au' },
      fields: ['address_components', 'geometry', 'formatted_address']
    });

    autocomplete.addListener('place_changed', () => {
      const place = autocomplete.getPlace();
      
      if (!place.geometry) {
        alert('Please select an address from the dropdown');
        return;
      }

      let city = '';
      for (let component of place.address_components) {
        if (component.types.includes('locality')) {
          city = component.long_name;
          break;
        }
      }

      setFormData(prev => ({
        ...prev,
        address: place.formatted_address,
        latitude: place.geometry.location.lat(),
        longitude: place.geometry.location.lng(),
        city: city
      }));
    });
  };

  useEffect(() => {
    if (!window.google) {
      const script = document.createElement('script');
      // Note: Replace with your Google API key
      script.src = `https://maps.googleapis.com/maps/api/js?key=AIzaSyDcyfbIGKgaP_uEoGdAgMtGdk-XLbKV2aY&libraries=places`;
      script.async = true;
      script.onload = () => initAutocomplete();
      document.head.appendChild(script);
    } else {
      initAutocomplete();
    }
  }, []);

  const handleSubmit = async (e) => {
  e.preventDefault();
  setLoading(true);

  // Auto-fix booking URL - add https:// if missing
  let bookingUrl = formData.booking_url;
  if (bookingUrl && !bookingUrl.startsWith('http://') && !bookingUrl.startsWith('https://')) {
    bookingUrl = 'https://' + bookingUrl;
  }

  try {
    const token = await getToken();
    
    const response = await fetch(`${API_URL}/api/admin/facilities`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        ...formData,
        booking_url: bookingUrl  // Use the fixed URL
      })
    });

      const data = await response.json();

      if (data.success) {
        setMessage({ type: 'success', text: `Success! Facility ${data.facility_id} created. Now create Clerk user with this facility_id.` });
        setFormData({
          facility_id: '', name: '', city: '', contact_name: '', 
          contact_email: '', contact_phone: '', booking_url: '',
          address: '', latitude: '', longitude: '',
          supported_types: [], features: [], rating: 4.0
        });
      } else {
        setMessage({ type: 'error', text: data.error });
      }
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setLoading(false);
    }
  };

  const toggleItem = (array, item) => {
    return array.includes(item) ? array.filter(i => i !== item) : [...array, item];
  };

  return (
        <div style={{ minHeight: '100vh', background: '#F3F4F6', padding: '40px 20px' }}>
  <div style={{ maxWidth: '800px', margin: '0 auto' }}>
    
    <div style={{ background: 'white', borderRadius: '16px', padding: '32px', marginBottom: '24px', textAlign: 'center' }}>
      <Building2 size={48} color={colors.primary} style={{ margin: '0 auto 16px' }} />
      <h1 style={{ fontSize: '32px', fontWeight: '800', marginBottom: '8px' }}>StorageScout Admin</h1>
      <p style={{ color: '#6B7280', marginBottom: '16px' }}>Add New Facility Partner</p>
      <Link to="/" style={{ color: colors.primary, fontSize: '14px', fontWeight: '600', textDecoration: 'none' }}>← Back to Portal</Link>
    </div>

    {/* ADD THIS TAB NAVIGATION HERE */}
    <div style={{ 
      background: 'white', 
      borderRadius: '12px', 
      padding: '8px', 
      marginBottom: '24px',
      display: 'flex',
      gap: '8px'
    }}>
      <button
        onClick={() => setActiveTab('facilities')}
        style={{
          flex: 1,
          padding: '12px 24px',
          background: activeTab === 'facilities' ? colors.primary : 'transparent',
          color: activeTab === 'facilities' ? 'white' : '#6B7280',
          border: 'none',
          borderRadius: '8px',
          fontSize: '14px',
          fontWeight: '700',
          cursor: 'pointer'
        }}
      >
        Add Facility
      </button>
      <button
        onClick={() => setActiveTab('analytics')}
        style={{
          flex: 1,
          padding: '12px 24px',
          background: activeTab === 'analytics' ? colors.primary : 'transparent',
          color: activeTab === 'analytics' ? 'white' : '#6B7280',
          border: 'none',
          borderRadius: '8px',
          fontSize: '14px',
          fontWeight: '700',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px'
        }}
      >
        <BarChart3 size={18} />
        Analytics
      </button>
    </div>

        {message.text && (
          <div style={{
            background: message.type === 'success' ? '#D1FAE5' : '#FEE2E2',
            padding: '16px', borderRadius: '12px', marginBottom: '24px',
            display: 'flex', alignItems: 'center', gap: '12px'
          }}>
            {message.type === 'success' ? <Check size={20} /> : <AlertCircle size={20} />}
            {message.text}
          </div>
        )}

    {activeTab === 'facilities' && (
        <form onSubmit={handleSubmit} style={{ background: 'white', borderRadius: '16px', padding: '32px' }}>
          
          <div style={{ marginBottom: '24px' }}>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>
              Facility ID *
            </label>
            <input
              value={formData.facility_id}
              onChange={(e) => setFormData({...formData, facility_id: e.target.value})}
              placeholder="MEL001"
              required
              style={{ width: '100%', padding: '12px', border: '2px solid #E5E7EB', borderRadius: '8px' }}
            />
          </div>

          <div style={{ marginBottom: '24px' }}>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>
              Facility Name *
            </label>
            <input
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              placeholder="Melbourne Central Storage"
              required
              style={{ width: '100%', padding: '12px', border: '2px solid #E5E7EB', borderRadius: '8px' }}
            />
          </div>

          <div style={{ marginBottom: '24px' }}>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>
              Address * (Start typing for autocomplete)
            </label>
            <input
              id="address-input"
              value={formData.address}
              onChange={(e) => setFormData({...formData, address: e.target.value})}
              placeholder="Start typing address..."
              required
              style={{ width: '100%', padding: '12px', border: '2px solid #E5E7EB', borderRadius: '8px' }}
            />
            {formData.latitude && (
              <p style={{ fontSize: '12px', color: '#10B981', marginTop: '4px' }}>
                ✓ Location captured: {formData.city}
              </p>
            )}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>City</label>
              <input
                value={formData.city}
                onChange={(e) => setFormData({...formData, city: e.target.value})}
                placeholder="Auto-filled"
                style={{ width: '100%', padding: '12px', border: '2px solid #E5E7EB', borderRadius: '8px' }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>Contact Name *</label>
              <input
                value={formData.contact_name}
                onChange={(e) => setFormData({...formData, contact_name: e.target.value})}
                placeholder="John Smith"
                required
                style={{ width: '100%', padding: '12px', border: '2px solid #E5E7EB', borderRadius: '8px' }}
              />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>Contact Email *</label>
              <input
                type="email"
                value={formData.contact_email}
                onChange={(e) => setFormData({...formData, contact_email: e.target.value})}
                placeholder="john@facility.com.au"
                required
                style={{ width: '100%', padding: '12px', border: '2px solid #E5E7EB', borderRadius: '8px' }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>Contact Phone *</label>
              <input
                type="tel"
                value={formData.contact_phone}
                onChange={(e) => {
                  // Only allow numbers and limit to 10 characters
                  const cleaned = e.target.value.replace(/[^0-9]/g, '');
                  setFormData({...formData, contact_phone: cleaned.slice(0, 10)});
                }}
                placeholder="0412345678"
                pattern="[0-9]{10}"
                maxLength="10"
                required
                style={{ width: '100%', padding: '12px', border: '2px solid #E5E7EB', borderRadius: '8px' }}
              />
              <p style={{ fontSize: '11px', color: '#6B7280', marginTop: '4px' }}>
                10 digits only (e.g., 0412345678)
              </p>
            </div>
          </div>

          <div style={{ marginBottom: '24px' }}>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>
              Booking URL *
            </label>
            <input
              value={formData.booking_url}
              onChange={(e) => setFormData({...formData, booking_url: e.target.value})}
              placeholder="https://facility.com.au/book?ref=storagescout"
              required
              style={{ width: '100%', padding: '12px', border: '2px solid #E5E7EB', borderRadius: '8px' }}
            />
          </div>

          <div style={{ marginBottom: '24px' }}>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>
              Storage Types Supported
            </label>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {[
                'Indoor Self Storage Units',
                'Outdoor Self Storage Units', 
                'Container Storage',
                'Warehouse (Pallet)',
                'Commercial/Business Storage',
                'Climate Control Storage',
                'Mobile/Portable Storage',
                'Document Storage'
              ].map(type => (
                <button
                  key={type}
                  type="button"
                  onClick={() => setFormData({...formData, supported_types: toggleItem(formData.supported_types, type)})}
                  style={{
                    padding: '8px 16px',
                    border: formData.supported_types.includes(type) ? `2px solid ${colors.primary}` : '2px solid #E5E7EB',
                    background: formData.supported_types.includes(type) ? `${colors.primary}20` : 'white',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: '600'
                  }}
                >
                  {type}
                </button>
              ))}
            </div>
          </div>

          <div style={{ marginBottom: '32px' }}>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>
              Features Available
            </label>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {[
                '24/7 Access',
                'Drive Up Access',
                'Access Control',
                'Security Cameras',
                'Climate Control',
                'Ground Floor Units'
              ].map(feature => (
                <button
                  key={feature}
                  type="button"
                  onClick={() => setFormData({...formData, features: toggleItem(formData.features, feature)})}
                  style={{
                    padding: '8px 16px',
                    border: formData.features.includes(feature) ? `2px solid ${colors.primary}` : '2px solid #E5E7EB',
                    background: formData.features.includes(feature) ? `${colors.primary}20` : 'white',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '13px'
                  }}
                >
                  {feature}
                </button>
              ))}
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%',
              padding: '16px',
              background: loading ? '#9CA3AF' : colors.primary,
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: '700',
              cursor: loading ? 'not-allowed' : 'pointer'
            }}
          >
            {loading ? 'Creating Facility...' : 'Create Facility'}
          </button>
        </form>
   )}

{activeTab === 'analytics' && (
  <AdminAnalytics />
)}

<div style={{
          background: '#FEF3C7',
          border: '1px solid #F59E0B',
          padding: '20px',
          borderRadius: '12px',
          marginTop: '24px',
          fontSize: '14px',
          color: '#92400E'
        }}>
          <strong>Next Steps After Creating Facility:</strong>
          <ol style={{ margin: '12px 0 0 20px', padding: 0 }}>
            <li>Go to clerk.com → Users → Create User</li>
            <li>Email: {formData.contact_email || '[contact email]'}</li>
            <li>Add metadata: <code>{`{"facility_id": "${formData.facility_id || 'FACILITY_ID'}"}`}</code></li>
            <li>Send welcome email to partner</li>
          </ol>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
